import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  Plus, 
  Minus,
  ExternalLink,
  Globe,
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Eye,
  Mail,
  MessageSquare,
  Target,
  Flag,
  Building2,
  Crown,
  Zap,
  Activity,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Star,
  StarOff,
  Bookmark,
  BookmarkCheck,
  Settings,
  EyeOff,
  Columns3,
  Square,
  CheckSquare,
  Hash,
  MousePointer,
  UserCheck,
  UserX,
  Send,
  Download,
  Trash2,
  Loader2,
  Paperclip,
  Copy
} from 'lucide-react';
import EmailPopup from './emailpop/EmailPopup';

// API BASE PATH
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Change Indicator Component
const ChangeIndicator = ({ current, previous, format = 'number' }) => {
  if (previous === null || previous === undefined) return null;
  
  const currentVal = parseFloat(current) || 0;
  const previousVal = parseFloat(previous) || 0;
  const change = currentVal - previousVal;
  const percentChange = previousVal > 0 ? ((change / previousVal) * 100) : 0;
  
  if (change === 0) return null;
  
  const formatNumber = (num) => {
    if (format === 'percent') {
      return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
    }
    if (Math.abs(num) >= 1000000) {
      return `${num > 0 ? '+' : ''}${(num / 1000000).toFixed(1)}M`;
    } else if (Math.abs(num) >= 1000) {
      return `${num > 0 ? '+' : ''}${(num / 1000).toFixed(1)}K`;
    }
    return `${num > 0 ? '+' : ''}${num.toFixed(0)}`;
  };

  return (
    <div className={`inline-flex items-center gap-1 text-xs font-medium ${
      change > 0 ? 'text-green-600' : 'text-red-600'
    }`}>
      {change > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      <span>{formatNumber(change)}</span>
      {percentChange !== 0 && (
        <span className="text-gray-500">({percentChange.toFixed(1)}%)</span>
      )}
    </div>
  );
};

// Tooltip Component for Geo Data
const GeoTooltip = ({ geoBreakdown, isVisible, position, onMouseEnter, onMouseLeave }) => {
  const tooltipRef = useRef(null);
  
  if (!isVisible || !geoBreakdown || geoBreakdown.length === 0) {
    return null;
  }

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div 
      ref={tooltipRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 max-w-xs animate-in fade-in duration-200"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%)',
        marginTop: '-8px'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">
          All Geographic Traffic
        </h4>
        <div className="max-h-64 overflow-y-auto space-y-2">
          {geoBreakdown.map((geo, index) => (
            <div key={index} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2 min-w-0">
                {geo.flag && (
                  <img 
                    src={geo.flag} 
                    alt={geo.country} 
                    className="w-5 h-3 flex-shrink-0" 
                  />
                )}
                <span className="text-xs text-gray-700 truncate">{geo.country}</span>
              </div>
              <div className="text-xs font-medium text-blue-600 ml-2">
                {formatNumber(geo.traffic)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute top-full left-1/2 transform -translate-x-1/2">
        <div className="border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-200"></div>
        <div className="border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-px"></div>
      </div>
    </div>
  );
};

const Toast = ({ message, type, isVisible, onClose }) => {
  if (!isVisible) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 text-white border-green-600';
      case 'error':
        return 'bg-red-500 text-white border-red-600';
      case 'warning':
        return 'bg-yellow-500 text-white border-yellow-600';
      default:
        return 'bg-blue-500 text-white border-blue-600';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border-l-4 min-w-96 ${getToastStyles()}`}>
        <span className="text-lg">{getIcon()}</span>
        <div className="flex-1">
          <p className="font-medium">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const AffiliateRecruitment = () => {
  // Toast functionality
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info'
  });

  const showNotification = (message, type = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });

    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };

  const hideNotification = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // NEW STATE VARIABLES FOR API DATA
  const [apiData, setApiData] = useState([]);
  const [existingAffiliateData, setExistingAffiliateData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [isLoadingExistingAffiliates, setIsLoadingExistingAffiliates] = useState(false);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Filter states
  const [selectedVerticals, setSelectedVerticals] = useState([]);
  const [selectedTrafficSources, setSelectedTrafficSources] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [existingAffiliate, setExistingAffiliate] = useState('');
  
  const [showResults, setShowResults] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [draftedItems, setDraftedItems] = useState(new Set());

  // Email popup state
  const [emailPopup, setEmailPopup] = useState({ isOpen: false, contact: null });

  // Tooltip states
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    url: true,
    totalTraffic: true,
    organicTraffic: true,
    paidTraffic: true,
    topGeos: true,
    contact: true
  });

  // Parse geo data helper function
  const parseGeoData = (geoString) => {
    if (!geoString) return [];
    
    const geos = geoString.split(', ').map(item => {
      const [country, traffic] = item.split(':');
      return {
        country: country.trim(),
        traffic: parseInt(traffic) || 0,
        flag: getCountryFlag(country.trim())
      };
    });
    
    return geos.sort((a, b) => b.traffic - a.traffic);
  };

  // Get country flag helper
  const getCountryFlag = (countryName) => {
const countryMap = {
  'United Kingdom of Great Britain and Northern Ireland': 'GB',
  'United States of America': 'US',
  'Germany': 'DE',
  'Canada': 'CA',
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'Slovenia': 'SI',
  'Afghanistan': 'AF',
  'Albania': 'AL',
  'Algeria': 'DZ',
  'Andorra': 'AD',
  'Angola': 'AO',
  'Antigua and Barbuda': 'AG',
  'Argentina': 'AR',
  'Armenia': 'AM',
  'Austria': 'AT',
  'Azerbaijan': 'AZ',
  'Bahamas': 'BS',
  'Bahrain': 'BH',
  'Bangladesh': 'BD',
  'Barbados': 'BB',
  'Belarus': 'BY',
  'Belgium': 'BE',
  'Belize': 'BZ',
  'Benin': 'BJ',
  'Bhutan': 'BT',
  'Bolivia (Plurinational State of)': 'BO',
  'Bosnia and Herzegovina': 'BA',
  'Botswana': 'BW',
  'Brazil': 'BR',
  'Brunei Darussalam': 'BN',
  'Bulgaria': 'BG',
  'Burkina Faso': 'BF',
  'Burundi': 'BI',
  'Cabo Verde': 'CV',
  'Cambodia': 'KH',
  'Cameroon': 'CM',
  'Central African Republic': 'CF',
  'Chad': 'TD',
  'Chile': 'CL',
  'China': 'CN',
  'Colombia': 'CO',
  'Comoros': 'KM',
  'Congo': 'CG',
  'Congo, Democratic Republic of the': 'CD',
  'Costa Rica': 'CR',
  "Côte d'Ivoire": 'CI',
  'Croatia': 'HR',
  'Cuba': 'CU',
  'Cyprus': 'CY',
  'Czechia': 'CZ',
  'Denmark': 'DK',
  'Djibouti': 'DJ',
  'Dominica': 'DM',
  'Dominican Republic': 'DO',
  'Ecuador': 'EC',
  'Egypt': 'EG',
  'El Salvador': 'SV',
  'Equatorial Guinea': 'GQ',
  'Eritrea': 'ER',
  'Estonia': 'EE',
  'Eswatini': 'SZ',
  'Ethiopia': 'ET',
  'Fiji': 'FJ',
  'Finland': 'FI',
  'France': 'FR',
  'Gabon': 'GA',
  'Gambia': 'GM',
  'Georgia': 'GE',
  'Ghana': 'GH',
  'Greece': 'GR',
  'Grenada': 'GD',
  'Guatemala': 'GT',
  'Guinea': 'GN',
  'Guinea-Bissau': 'GW',
  'Guyana': 'GY',
  'Haiti': 'HT',
  'Honduras': 'HN',
  'Hungary': 'HU',
  'Iceland': 'IS',
  'India': 'IN',
  'Indonesia': 'ID',
  'Iran (Islamic Republic of)': 'IR',
  'Iraq': 'IQ',
  'Ireland': 'IE',
  'Israel': 'IL',
  'Italy': 'IT',
  'Jamaica': 'JM',
  'Japan': 'JP',
  'Jordan': 'JO',
  'Kazakhstan': 'KZ',
  'Kenya': 'KE',
  'Kiribati': 'KI',
  "Korea (Democratic People's Republic of)": 'KP',
  'Korea, Republic of': 'KR',
  'Kuwait': 'KW',
  'Kyrgyzstan': 'KG',
  "Lao People's Democratic Republic": 'LA',
  'Latvia': 'LV',
  'Lebanon': 'LB',
  'Lesotho': 'LS',
  'Liberia': 'LR',
  'Libya': 'LY',
  'Liechtenstein': 'LI',
  'Lithuania': 'LT',
  'Luxembourg': 'LU',
  'Madagascar': 'MG',
  'Malawi': 'MW',
  'Malaysia': 'MY',
  'Maldives': 'MV',
  'Mali': 'ML',
  'Malta': 'MT',
  'Marshall Islands': 'MH',
  'Mauritania': 'MR',
  'Mauritius': 'MU',
  'Mexico': 'MX',
  'Micronesia (Federated States of)': 'FM',
  'Moldova, Republic of': 'MD',
  'Monaco': 'MC',
  'Mongolia': 'MN',
  'Montenegro': 'ME',
  'Morocco': 'MA',
  'Mozambique': 'MZ',
  'Myanmar': 'MM',
  'Namibia': 'NA',
  'Nauru': 'NR',
  'Nepal': 'NP',
  'Netherlands': 'NL',
  'Nicaragua': 'NI',
  'Niger': 'NE',
  'Nigeria': 'NG',
  'North Macedonia': 'MK',
  'Norway': 'NO',
  'Oman': 'OM',
  'Pakistan': 'PK',
  'Palau': 'PW',
  'Palestine, State of': 'PS',
  'Panama': 'PA',
  'Papua New Guinea': 'PG',
  'Paraguay': 'PY',
  'Peru': 'PE',
  'Philippines': 'PH',
  'Poland': 'PL',
  'Portugal': 'PT',
  'Qatar': 'QA',
  'Romania': 'RO',
  'Russian Federation': 'RU',
  'Rwanda': 'RW',
  'Saint Kitts and Nevis': 'KN',
  'Saint Lucia': 'LC',
  'Saint Vincent and the Grenadines': 'VC',
  'Samoa': 'WS',
  'San Marino': 'SM',
  'Sao Tome and Principe': 'ST',
  'Saudi Arabia': 'SA',
  'Senegal': 'SN',
  'Serbia': 'RS',
  'Seychelles': 'SC',
  'Sierra Leone': 'SL',
  'Singapore': 'SG',
  'Solomon Islands': 'SB',
  'Somalia': 'SO',
  'South Africa': 'ZA',
  'South Sudan': 'SS',
  'Spain': 'ES',
  'Sri Lanka': 'LK',
  'Sudan': 'SD',
  'Suriname': 'SR',
  'Sweden': 'SE',
  'Switzerland': 'CH',
  'Syrian Arab Republic': 'SY',
  'Tajikistan': 'TJ',
  'Thailand': 'TH',
  'Timor-Leste': 'TL',
  'Togo': 'TG',
  'Tonga': 'TO',
  'Trinidad and Tobago': 'TT',
  'Tunisia': 'TN',
  'Turkey': 'TR',
  'Turkmenistan': 'TM',
  'Tuvalu': 'TV',
  'Uganda': 'UG',
  'Ukraine': 'UA',
  'United Arab Emirates': 'AE',
  'Uruguay': 'UY',
  'Uzbekistan': 'UZ',
  'Vanuatu': 'VU',
  'Venezuela (Bolivarian Republic of)': 'VE',
  'Viet Nam': 'VN',
  'Yemen': 'YE',
  'Zambia': 'ZM',
  'Zimbabwe': 'ZW'
};

    
    const code = countryMap[countryName] || null;
    return code ? `https://flagsapi.com/${code}/flat/24.png` : null;
  };


  // Transform API data to component format
  const transformApiData = (data) => {
    return data.map((item, index) => ({
      id: index + 1,
      url: item.affiliate_url,
      totalTraffic: parseInt(item.total_traffic) || 0,
      organicTraffic: parseInt(item.organic_traffic) || 0,
      paidTraffic: parseInt(item.adwords_traffic) || 0,
      prevTotalTraffic: item.prev_total_traffic ? parseInt(item.prev_total_traffic) : null,
      prevOrganicTraffic: item.prev_organic_traffic ? parseInt(item.prev_organic_traffic) : null,
      prevPaidTraffic: item.prev_adwords_traffic ? parseInt(item.prev_adwords_traffic) : null,
      organicTrafficChange: item.organic_traffic_change || 0,
      paidTrafficChange: item.adwords_traffic_change || 0,
      totalTrafficChange: parseInt(item.total_traffic_change) || 0,
      hasHistory: item.has_history || false,
      geoBreakdown: parseGeoData(item.top_geo_organic_traffic),
      prevGeoBreakdown: item.prev_top_geo_organic_traffic ? parseGeoData(item.prev_top_geo_organic_traffic) : null,
      keywords: item.keywords ? item.keywords.split(',').map(k => ({ keyword: k.trim() })) : [],
      topPages: item.top_page ? { all: item.top_page.split(',').map(p => p.trim()) } : { all: [] },
      topPaidKeywords: item.paid_keyword ? { all: item.paid_keyword.split(',').map(k => ({ keyword: k.trim() })) } : { all: [] },
      hasEmail: (item.email_array && item.email_array.length > 0) || item.email,
      allEmails: item.email_array || (item.email ? [item.email] : []),
      alreadyApproached: false,
      verticals: item.vertical ? item.vertical.split(',').map(v => parseInt(v)) : []
    }));
  };

  // FETCH EXISTING AFFILIATES FUNCTION
  const fetchExistingAffiliate = async () => {
    setIsLoadingExistingAffiliates(true);
    setApiError(null);
    
    try {
      const existingresponse = await fetch(`${basePath}/api/affiliate-recruitment/existing`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!existingresponse.ok) {
        throw new Error(`HTTP error! status: ${existingresponse.status}`);
      }

      const responseData = await existingresponse.json();
      
      if (responseData.success) {
        setExistingAffiliateData(responseData.data.affiliates || []);
        setCountryData(responseData.data.countries || []);
        
        
      } else {
        throw new Error(responseData.message || 'Failed to fetch data');
      }
      
    } catch (error) {
      console.error("Error fetching existing affiliates:", error);
      setApiError(error.message);
      
      showNotification(`Failed to load existing affiliates: ${error.message}`, 'error');
      
      // Fallback to mock data if API fails
      setExistingAffiliateData([
        { name: 'Monster Casino', value: 'monstercasino.com' },
        { name: 'Swift Casino', value: 'swiftcasino.com' },
        { name: 'Royal Bingo', value: 'royalbingo.com' },
        { name: 'Lucky Sports', value: 'luckysports.com' },
        { name: 'Vegas Poker', value: 'vegaspoker.com' }
      ]);
      
      setCountryData([
        { code: 'GB', name: 'United Kingdom' },
        { code: 'US', name: 'United States' },
        { code: 'DE', name: 'Germany' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' }
      ]);
    } finally {
      setIsLoadingExistingAffiliates(false);
    }
  };

  // RETRY FUNCTION
  const retryFetchExistingAffiliates = () => {
    fetchExistingAffiliate();
  };

  // FETCH DATA ON COMPONENT MOUNT
  useEffect(() => {
    fetchExistingAffiliate();
  }, []);

  // Mock filter options (UPDATED TO USE API DATA)
  const filterOptions = {
    verticals: ['Casino', 'Sports Betting', 'Poker', 'Lottery', 'Bingo', 'Sweepstake', 'Crypto'],
    trafficSource: ['Organic Traffic', 'Paid Traffic'],
    existingAffiliate: existingAffiliateData,
    countries: countryData.map(country => ({
      name: country.name,
      value: country.name,
      flag: `https://flagsapi.com/${country.code}/flat/24.png`
    })),
    existingAffiliatesOptions: ['Yes', 'No']
  };

  // Format number utility
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Handle tooltip mouse events
  const handleGeoMouseEnter = (e, geoBreakdown) => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    setTooltipData(geoBreakdown);
    setTooltipVisible(true);
  };

  const handleGeoMouseLeave = () => {
    // Add a small delay before hiding to prevent flickering
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipVisible(false);
      setTooltipData(null);
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    // Clear the timeout when mouse enters tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    // Hide tooltip when mouse leaves
    setTooltipVisible(false);
    setTooltipData(null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Email popup handlers
  const openEmailPopup = (affiliate) => {
    const contact = {
      email: affiliate.allEmails && affiliate.allEmails.length > 0 ? affiliate.allEmails[0] : '',
      name: affiliate.url,
      url: affiliate.url,
      affiliateData: affiliate,
      allEmails: affiliate.allEmails || []
    };
    
    setEmailPopup({ isOpen: true, contact });
  };

  const closeEmailPopup = () => {
    setEmailPopup({ isOpen: false, contact: null });
  };

  const handleEmailSend = async (emailData, recipient) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showNotification('Email sent successfully!', 'success');
      closeEmailPopup();
      
    } catch (error) {
      console.error('Error sending email:', error);
      showNotification('Failed to send email', 'error');
    }
  };

  // Toggle draft functionality
  const toggleDraft = (affiliateId) => {
    setDraftedItems(prev => {
      const newDrafted = new Set(prev);
      if (newDrafted.has(affiliateId)) {
        newDrafted.delete(affiliateId);
      } else {
        newDrafted.add(affiliateId);
      }
      return newDrafted;
    });
  };

  // Custom email templates for affiliate outreach
  const affiliateEmailTemplates = [
    {
      name: "Partnership Opportunity",
      subject: "Partnership Opportunity - Website Affiliate Program",
      message: `Hello,

I hope this email finds you well. My name is [Your Name], and I'm reaching out from [Your Website/Company Name].

I've been following your work and really admire what you've built with your website. Your content quality and audience engagement are impressive.

I'm writing to explore a potential affiliate partnership opportunity. We're looking to collaborate with quality websites like yours to create mutually beneficial relationships.

Could you please share:
• Your current affiliate program criteria and requirements
• Commission structure and payment terms
• Any specific content or promotional guidelines
• Minimum traffic or performance thresholds

I'd be happy to provide more details about our program and discuss how we can work together effectively.

Looking forward to hearing from you.

Best regards,
[Your Name]
[Your Title]
[Your Contact Information]`
    }
  ];

  // Get current data based on API data or empty array
  const currentData = useMemo(() => apiData, [apiData]);

  // Column helper for type safety
  const columnHelper = createColumnHelper();

  // Define table columns with proper memoization
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'actions',
      header: () => (
        <div className="flex items-center justify-center gap-2">
          <span className="text-xs font-medium text-blue-600 tracking-wider">Actions</span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => row.toggleExpanded()}
            className={`text-blue-600 hover:text-blue-800 transition-all duration-200 transform hover:scale-110 ${
              row.getIsExpanded() ? 'rotate-180' : ''
            }`}
          >
            {row.getIsExpanded() ? 
              <Minus className="w-4 h-4" /> : 
              <Plus className="w-4 h-4" />
            }
          </button>
          <button
            onClick={() => toggleDraft(row.original.id)}
            className={`transition-colors ${
              draftedItems.has(row.original.id)
                ? 'text-blue-600 hover:text-blue-800'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {draftedItems.has(row.original.id) ? 
              <BookmarkCheck className="w-4 h-4" /> : 
              <Bookmark className="w-4 h-4" />
            }
          </button>
        </div>
      ),
      enableSorting: false,
    }),
    columnHelper.accessor('url', {
      header: ({ column }) => (
        <button 
          onClick={() => column.toggleSorting()}
          className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full"
        >
          <Globe className="w-4 h-4" />
          Affiliate URL
          {column.getIsSorted() === 'desc' ? (
            <ArrowDown className="w-4 h-4 text-blue-600" />
          ) : column.getIsSorted() === 'asc' ? (
            <ArrowUp className="w-4 h-4 text-blue-600" />
          ) : (
            <ArrowUpDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      ),
      cell: (info) => (
        <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer transition-colors text-center">
          {info.getValue()}
        </div>
      ),
      sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor('totalTraffic', {
      header: ({ column }) => (
        <button 
          onClick={() => column.toggleSorting()}
          className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full"
        >
          <BarChart3 className="w-4 h-4" />
          Total Traffic
          {column.getIsSorted() === 'desc' ? (
            <ArrowDown className="w-4 h-4 text-blue-600" />
          ) : column.getIsSorted() === 'asc' ? (
            <ArrowUp className="w-4 h-4 text-blue-600" />
          ) : (
            <ArrowUpDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      ),
      cell: (info) => {
        const current = info.getValue();
        const previous = info.row.original.prevTotalTraffic;
        return (
          <div className="space-y-1 text-center">
            <div className="text-sm font-medium text-gray-900">
              {formatNumber(current)}
            </div>
            {info.row.original.hasHistory && (
              <ChangeIndicator current={current} previous={previous} />
            )}
          </div>
        );
      },
      sortingFn: 'basic',
    }),
    columnHelper.accessor('organicTraffic', {
      header: ({ column }) => (
        <button 
          onClick={() => column.toggleSorting()}
          className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full"
        >
          <TrendingUp className="w-4 h-4" />
          Organic Traffic
          {column.getIsSorted() === 'desc' ? (
            <ArrowDown className="w-4 h-4 text-blue-600" />
          ) : column.getIsSorted() === 'asc' ? (
            <ArrowUp className="w-4 h-4 text-blue-600" />
          ) : (
            <ArrowUpDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      ),
      cell: (info) => {
        const current = info.getValue();
        const previous = info.row.original.prevOrganicTraffic;
        return (
          <div className="space-y-1 text-center">
            <div className="text-sm font-medium text-blue-600">
              {formatNumber(current)}
            </div>
            {info.row.original.hasHistory && (
              <ChangeIndicator current={current} previous={previous} />
            )}
          </div>
        );
      },
      sortingFn: 'basic',
    }),
    columnHelper.accessor('paidTraffic', {
      header: ({ column }) => (
        <button 
          onClick={() => column.toggleSorting()}
          className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full"
        >
          <MousePointer className="w-4 h-4" />
          Paid Traffic
          {column.getIsSorted() === 'desc' ? (
            <ArrowDown className="w-4 h-4 text-blue-600" />
          ) : column.getIsSorted() === 'asc' ? (
            <ArrowUp className="w-4 h-4 text-blue-600" />
          ) : (
            <ArrowUpDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      ),
      cell: (info) => {
        const current = info.getValue();
        const previous = info.row.original.prevPaidTraffic;
        return (
          <div className="space-y-1 text-center">
            <div className="text-sm font-medium text-blue-600">
              {formatNumber(current)}
            </div>
            {info.row.original.hasHistory && previous !== null && (
              <ChangeIndicator current={current} previous={previous} />
            )}
          </div>
        );
      },
      sortingFn: 'basic',
    }),
    columnHelper.accessor('geoBreakdown', {
      id: 'topGeos',
      header: () => (
        <div className="flex items-center justify-center gap-2">
          <Flag className="w-4 h-4" />
          <span className="text-xs font-medium text-blue-600 tracking-wider">Top Geos</span>
        </div>
      ),
      cell: (info) => {
        const geoBreakdown = info.getValue();
        return (
          <div 
            className="relative group cursor-pointer"
            onMouseEnter={(e) => handleGeoMouseEnter(e, geoBreakdown)}
            onMouseLeave={handleGeoMouseLeave}
          >
            <div className="inline-flex items-center gap-1 text-xs">
              {geoBreakdown?.slice(0, 2).map((geo, index) => (
                <span key={geo.country} className="inline-flex items-center gap-1">
                  {index > 0 && <span className="text-gray-400">, </span>}
                  <span className="text-gray-700">
                    {geo.country === 'United Kingdom of Great Britain and Northern Ireland' ? 'UK' : 
                     geo.country === 'United States of America' ? 'US' :
                     geo.country.length > 15 ? geo.country.substring(0, 12) + '...' : geo.country}
                  </span>
                  <span className="text-blue-600 font-medium">[{formatNumber(geo.traffic)}]</span>
                </span>
              ))}
              {(geoBreakdown?.length || 0) > 2 && (
                <span className="text-gray-500 ml-1 underline decoration-dotted">
                  , +{geoBreakdown.length - 2} more
                </span>
              )}
            </div>
          </div>
        );
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'contact',
      header: () => (
        <div className="flex items-center justify-center gap-2">
          <Mail className="w-4 h-4" />
          <span className="text-xs font-medium text-blue-600 tracking-wider">Contact</span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="space-y-1">
          <button 
            onClick={() => openEmailPopup(row.original)}
            disabled={!row.original.hasEmail}
            className={`${
              row.original.hasEmail 
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30' 
                : 'bg-gray-300 cursor-not-allowed'
            } text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto`}
          >
            <Mail className="w-3 h-3" />
            Contact
          </button>
          <div className="text-xs text-center">
            {row.original.hasEmail ? (
              <span className="text-blue-600 font-medium">
                {row.original.allEmails?.length || 1} email{row.original.allEmails?.length > 1 ? 's' : ''} found
              </span>
            ) : (
              <span className="text-gray-500">No email available</span>
            )}
          </div>
          {row.original.alreadyApproached && (
            <div className="text-xs text-green-600 font-medium text-center">Contacted</div>
          )}
        </div>
      ),
      enableSorting: false,
    }),
  ], [draftedItems, formatNumber, handleGeoMouseEnter, handleGeoMouseLeave, openEmailPopup, toggleDraft]);

  // Create table instance with maximum performance optimization
  const table = useReactTable({
    data: currentData,
    columns,
    state: {
      globalFilter,
      columnVisibility,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableColumnFilters: false,
    enableGlobalFilter: true,
    enableSorting: true,
    initialState: {
      pagination: {
        pageSize: 10,
      },
      sorting: [
        {
          id: 'totalTraffic',
          desc: true,
        },
      ],
    },
    sortingFns: {
      basic: (rowA, rowB, columnId) => {
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);
        
        if (a === b) return 0;
        if (a == null) return 1;
        if (b == null) return -1;
        
        return a < b ? -1 : 1;
      },
    },
    meta: {
      updateData: () => {},
    },
    manualPagination: false,
    manualSorting: false,
    manualFiltering: false,
  });

  // Simple Content Box Component (without tabs)
  const SimpleContentBox = ({ title, icon, data, renderContent }) => {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:scale-[1.02] h-full flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              {icon}
            </div>
            <h4 className="text-base font-medium text-white">{title}</h4>
          </div>
        </div>

        <div className="p-4 flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto">
            {renderContent()}
          </div>
        </div>
      </div>
    );
  };

  // Simple Multi-Select Component
  const MultiSelectFilter = ({ label, options, selectedValues, onChange, renderOption = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const filteredOptions = options.filter(option => {
      const searchText = typeof option === 'string' ? option : (option.name || option.value || '');
      return searchText.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const toggleOption = (option) => {
      const value = typeof option === 'string' ? option : (option.value || option.name);
      if (selectedValues.includes(value)) {
        onChange(selectedValues.filter(v => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    };

    const getDisplayText = () => {
      if (selectedValues.length === 0) return `Select ${label}...`;
      if (selectedValues.length === 1) return selectedValues[0];
      return `${selectedValues.length} selected`;
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
            isOpen ? 'border-blue-500 ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'
          }`}
        >
          <span className={`text-sm truncate ${
            selectedValues.length === 0 ? 'text-gray-500' : 'text-gray-900'
          }`}>
            {getDisplayText()}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl animate-in slide-in-from-top-2 duration-200">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500">No options found</div>
              ) : (
                filteredOptions.map((option, index) => {
                  const value = typeof option === 'string' ? option : (option.value || option.name);
                  const isSelected = selectedValues.includes(value);
                  
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => toggleOption(option)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-blue-50 transition-colors duration-150 text-left ${
                        isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {renderOption ? renderOption(option) : (
                          <span className="truncate">
                            {typeof option === 'string' ? option : (option.name || option.value)}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {selectedValues.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {selectedValues.map((value, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs animate-in slide-in-from-left-2 duration-200"
              >
                <span className="truncate max-w-24">{value}</span>
                <button
                  type="button"
                  onClick={() => onChange(selectedValues.filter(v => v !== value))}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors flex-shrink-0"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Simple Single-Select Component
  const SingleSelectFilter = ({ label, options, selectedValue, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const getDisplayText = () => {
      if (!selectedValue) return `Select ${label}...`;
      return selectedValue;
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
            isOpen ? 'border-blue-500 ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'
          }`}
        >
          <span className={`text-sm truncate ${
            !selectedValue ? 'text-gray-500' : 'text-gray-900'
          }`}>
            {getDisplayText()}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl animate-in slide-in-from-top-2 duration-200">
            <div className="max-h-60 overflow-y-auto">
              {options.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center px-4 py-3 text-sm hover:bg-blue-50 transition-colors duration-150 text-left ${
                    selectedValue === option ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span className="truncate">{option}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle form submission
  const handleSubmit = async () => {
    
    setIsLoadingTableData(true);
    
    try {
      const tableDataFetch = await fetch(`${basePath}/api/affiliate-recruitment/fetchData`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verticals: selectedVerticals,
          trafficSources: selectedTrafficSources,
          brands: selectedBrands,
          countries: selectedCountries,
          existingAffiliate: existingAffiliate
        })
      });

      if (!tableDataFetch.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const responseData = await tableDataFetch.json();
      
      if (responseData.success && responseData.data) {
        // Transform the API data to match component format
        const transformedData = transformApiData(responseData.data);
        setApiData(transformedData);
        setShowResults(true);
        showNotification(`Found ${transformedData.length} affiliates`, 'success');
      } else {
        throw new Error(responseData.message || 'No data received');
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(`Failed to fetch data: ${error.message}`, 'error');
      
      // Set empty data on error
      setApiData([]);
      setShowResults(true);
    } finally {
      setIsLoadingTableData(false);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedVerticals([]);
    setSelectedTrafficSources([]);
    setSelectedBrands('');
    setSelectedCountries([]);
    setExistingAffiliate('');
    setGlobalFilter('');
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      
      {/* Geo Tooltip */}
      <GeoTooltip 
        geoBreakdown={tooltipData} 
        isVisible={tooltipVisible} 
        position={tooltipPosition}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
      />
      
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800">
                Affiliate Recruitment
              </h1>
              <p className="text-gray-600 leading-relaxed max-w-xl">
                Discover and recruit high-performing affiliates with advanced filtering and analytics
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 shadow-sm">
                <span className="text-sm font-medium text-blue-600">Premium Tools</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-medium text-gray-800">Advanced Search Filters</h2>
            </div>
            
            <button
              onClick={clearAllFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Verticals Filter */}
            <MultiSelectFilter
              label="Verticals"
              options={filterOptions.verticals}
              selectedValues={selectedVerticals}
              onChange={setSelectedVerticals}
            />

            {/* Traffic Source Filter */}
            <MultiSelectFilter
              label="Traffic Source"
              options={filterOptions.trafficSource}
              selectedValues={selectedTrafficSources}
              onChange={setSelectedTrafficSources}
            />

            {/* Existing Affiliate Brands Filter */}
            <div className="relative">
              <SingleSelectFilter
                label="Existing Affiliate Brands"
                options={filterOptions.existingAffiliate.map(brand => brand.name || brand)}
                selectedValue={selectedBrands}
                onChange={setSelectedBrands}
              />
              
              {/* Loading indicator */}
              {isLoadingExistingAffiliates && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-sm p-3 z-50">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading existing affiliates...
                  </div>
                </div>
              )}
              
              {/* Error state with retry */}
              {apiError && !isLoadingExistingAffiliates && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-red-200 rounded-lg shadow-sm p-3 z-50">
                  <div className="text-sm text-red-600 mb-2">Failed to load affiliates</div>
                  <button
                    onClick={retryFetchExistingAffiliates}
                    className="text-xs bg-red-50 hover:bg-red-100 text-red-700 px-2 py-1 rounded border border-red-200 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>

            {/* Country Filter */}
            <MultiSelectFilter
              label="Countries"
              options={filterOptions.countries}
              selectedValues={selectedCountries}
              onChange={setSelectedCountries}
              renderOption={(country) => (
                <div className="flex items-center gap-2 min-w-0">
                  <img 
                    src={country.flag} 
                    alt={country.name}
                    className="w-6 h-4 flex-shrink-0"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="16" viewBox="0 0 24 16"><rect width="24" height="16" fill="%23ddd"/></svg>';
                    }}
                  />
                  <span className="text-sm truncate">{country.name || country}</span>
                </div>
              )}
            />

            {/* Existing Affiliates Yes/No */}
            <SingleSelectFilter
              label="Already Approached"
              options={filterOptions.existingAffiliatesOptions}
              selectedValue={existingAffiliate}
              onChange={setExistingAffiliate}
            />

            {/* Submit Button */}
            <div className="flex items-end">
              <button
                onClick={handleSubmit}
                disabled={isLoadingTableData}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoadingTableData ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>Search Affiliates</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Table */}
      {showResults && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-lg font-medium text-gray-800">Search Results</h2>
                  <p className="text-sm text-gray-600">
                    {table.getFilteredRowModel().rows.length} affiliates found
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                
                {/* Enhanced Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by URL, keywords..."
                    value={globalFilter ?? ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="pl-10 pr-12 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-64 transition-all duration-200 hover:shadow-md focus:shadow-md"
                  />
                </div>

                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:shadow-md"
                >
                  {[5, 10, 25, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize} per page
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => {/* Column visibility toggle logic can be added here */}}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 border border-gray-200 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-md"
                >
                  <Settings className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Settings</span>
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th key={header.id} className="px-6 py-4 text-center whitespace-nowrap">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoadingTableData ? (
                  <tr>
                    <td colSpan={table.getAllColumns().length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                        <p>Loading results...</p>
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={table.getAllColumns().length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Search className="w-8 h-8 text-gray-400" />
                        <p>No results found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <React.Fragment key={row.id}>
                      <tr className={`hover:bg-gray-50 transition-all duration-200 ${
                        row.getIsExpanded() ? 'bg-blue-50' : ''
                      }`}>
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-center">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>

                      {/* Expanded Row Content - Only render if expanded */}
                      {row.getIsExpanded() && (
                        <tr className="animate-in slide-in-from-top-4 duration-300">
                          <td colSpan={table.getAllColumns().length} className="px-6 py-6 bg-gradient-to-r from-blue-50 to-gray-50">
                            <div className="max-w-full overflow-hidden">
                              <div className="space-y-6">
                                
                                {/* Country Breakdown with Historical Data */}
                                {row.original.geoBreakdown && row.original.geoBreakdown.length > 0 && (
                                  <div className="transform transition-all duration-500 hover:scale-[1.02]">
                                    <h4 className="text-xs font-medium text-gray-800 mb-3 flex items-center gap-2">
                                      <Flag className="w-4 h-4 text-blue-600" />
                                      Country Traffic Breakdown
                                    </h4>
                                    <div className="w-full">
                                      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full">
                                            <thead className="bg-gray-100">
                                              <tr>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Country</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Current Traffic</th>
                                                {row.original.hasHistory && (
                                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 tracking-wider whitespace-nowrap">Change</th>
                                                )}
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                              {row.original.geoBreakdown.map((geo, index) => {
                                                // Find previous data for this country
                                                const prevGeo = row.original.prevGeoBreakdown?.find(
                                                  p => p.country === geo.country
                                                );
                                                
                                                return (
                                                  <tr key={`${row.id}-geo-${index}`} className="hover:bg-blue-50 transition-colors duration-200">
                                                    <td className="px-4 py-3 text-xs font-medium text-gray-900 whitespace-nowrap text-center">
                                                      <div className="flex items-center justify-center gap-2">
                                                        {geo.flag && (
                                                          <img src={geo.flag} alt={geo.country} className="w-6 h-4" />
                                                        )}
                                                        {geo.country === 'United Kingdom of Great Britain and Northern Ireland' ? 'United Kingdom' : geo.country}
                                                      </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-blue-600 font-medium whitespace-nowrap text-center">
                                                      {formatNumber(geo.traffic)}
                                                    </td>
                                                    {row.original.hasHistory && (
                                                      <td className="px-4 py-3 text-xs whitespace-nowrap text-center">
                                                        {prevGeo && <ChangeIndicator current={geo.traffic} previous={prevGeo.traffic} />}
                                                      </td>
                                                    )}
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                  
                                  {/* Top Pages */}
                                  {row.original.topPages && row.original.topPages.all.length > 0 && (
                                    <SimpleContentBox
                                      title="Top Pages"
                                      icon={<Eye className="w-5 h-5 text-white" />}
                                      data={row.original.topPages}
                                      renderContent={() => (
                                        <div className="space-y-3">
                                          {row.original.topPages.all?.map((page, index) => (
                                            <div key={`${row.id}-page-${index}`} className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer border border-transparent hover:border-blue-200">
                                              <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-lg">
                                                {index + 1}
                                              </span>
                                              <span className="text-xs text-gray-700 leading-tight font-medium">{page}</span>
                                            </div>
                                          ))}
                                          {row.original.topPages.all.length === 0 && (
                                            <div className="text-xs text-gray-500 text-center">No page data available</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                  )}

                                  {/* Top Organic Keywords */}
                                  {row.original.keywords && row.original.keywords.length > 0 && (
                                    <SimpleContentBox
                                      title="Top Organic Keywords"
                                      icon={<Target className="w-5 h-5 text-white" />}
                                      data={row.original.keywords}
                                      renderContent={() => (
                                        <div className="space-y-3">
                                          {row.original.keywords?.map((keyword, index) => (
                                            <div key={`${row.id}-keyword-${index}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 border border-transparent hover:border-blue-200">
                                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-lg">
                                                  {index + 1}
                                                </span>
                                                <span className="text-xs text-gray-700 break-words font-medium">{keyword.keyword}</span>
                                              </div>
                                            </div>
                                          ))}
                                          {row.original.keywords.length === 0 && (
                                            <div className="text-xs text-gray-500 text-center">No keyword data available</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                  )}

                                  {/* AdWords Keywords */}
                                  {row.original.topPaidKeywords && row.original.topPaidKeywords.all.length > 0 && (
                                    <SimpleContentBox
                                      title="Paid Keywords"
                                      icon={<BarChart3 className="w-5 h-5 text-white" />}
                                      data={row.original.topPaidKeywords}
                                      renderContent={() => (
                                        <div className="space-y-3">
                                          {row.original.topPaidKeywords.all?.map((keyword, index) => (
                                            <div key={`${row.id}-paid-keyword-${index}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 border border-transparent hover:border-blue-200">
                                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 shadow-lg">
                                                  {index + 1}
                                                </span>
                                                <span className="text-xs text-gray-700 break-words font-medium">{keyword.keyword}</span>
                                              </div>
                                            </div>
                                          ))}
                                          {row.original.topPaidKeywords.all.length === 0 && (
                                            <div className="text-xs text-gray-500 text-center">No paid keyword data available</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)} of {table.getFilteredRowModel().rows.length} results
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    !table.getCanPreviousPage()
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-white hover:shadow-sm transform hover:scale-105'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
                    const pageIndex = table.getState().pagination.pageIndex;
                    const pageCount = table.getPageCount();
                    let pageNumber;
                    
                    if (pageCount <= 5) {
                      pageNumber = i;
                    } else if (pageIndex <= 2) {
                      pageNumber = i;
                    } else if (pageIndex >= pageCount - 3) {
                      pageNumber = pageCount - 5 + i;
                    } else {
                      pageNumber = pageIndex - 2 + i;
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => table.setPageIndex(pageNumber)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          pageIndex === pageNumber
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-600/30'
                            : 'text-gray-700 hover:bg-white hover:shadow-sm transform hover:scale-105'
                        }`}
                      >
                        {pageNumber + 1}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    !table.getCanNextPage()
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-white hover:shadow-sm transform hover:scale-105'
                  }`}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Popup */}
      <EmailPopup
        isOpen={emailPopup.isOpen}
        onClose={closeEmailPopup}
        recipient={emailPopup.contact}
        onSend={handleEmailSend}
        templates={affiliateEmailTemplates}
        customTitle={emailPopup.contact ? `Contact ${emailPopup.contact.name}` : ''}
        showTemplateSelection={true}
        allowAttachments={true}
        allowCc={true}
        readOnlyTo={true}
      />
      
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};

export default AffiliateRecruitment;